/**
 * MusicStemPanel - ボーカル／インストの音量ミキサー
 *
 * 歌詞パネルと同じ領域にタブとして表示する。
 * 音量を動かすだけで再生方式（要素再生／ミックス再生）は music-playback.ts が切り替える。
 */

import { AudioLines, Loader2, Music, TriangleAlert, VolumeOff } from 'lucide-react'
import { useCallback, useRef } from 'react'

import { Switch } from '@/components/ui/switch'
import { resolveStemPlan } from '@/lib/music-stem-plan'
import { cn } from '@/lib/utils'
import { useMusicStore } from '@/store/useMusicStore'

const SEGMENTS = 12

/** 縦スライダー（セグメント付きレベルメーター風） */
const VerticalSlider = ({
  value,
  onChange,
  label,
  muted,
  color,
  disabled,
}: {
  value: number
  onChange: (volume: number) => void
  label: string
  muted: boolean
  color: string
  disabled: boolean
}) => {
  const trackRef = useRef<HTMLDivElement>(null)

  const handlePointer = useCallback(
    (clientY: number) => {
      const track = trackRef.current
      if (!track) return
      const rect = track.getBoundingClientRect()
      const ratio = 1 - Math.max(0, Math.min(1, (clientY - rect.top) / rect.height))
      onChange(Math.round(ratio * 100) / 100)
    },
    [onChange],
  )

  const activeSegments = Math.round(value * SEGMENTS)

  return (
    <div
      ref={trackRef}
      className={cn(
        'relative flex touch-none flex-col-reverse gap-1 rounded-xl bg-white/4 px-2 py-2',
        disabled ? 'pointer-events-none opacity-40' : 'cursor-pointer',
      )}
      role="slider"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={1}
      aria-valuenow={value}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onPointerDown={(e) => {
        handlePointer(e.clientY)
        const el = e.currentTarget
        el.setPointerCapture(e.pointerId)
        const onMove = (ev: PointerEvent) => handlePointer(ev.clientY)
        const onUp = () => {
          el.removeEventListener('pointermove', onMove)
          el.removeEventListener('pointerup', onUp)
        }
        el.addEventListener('pointermove', onMove)
        el.addEventListener('pointerup', onUp)
      }}
    >
      {Array.from({ length: SEGMENTS }, (_, i) => {
        const active = i < activeSegments
        const segColor = muted ? 'rgba(239,68,68,0.7)' : color
        return (
          <div
            key={i}
            className="h-2.5 w-10 rounded-sm transition-all duration-100"
            style={{
              background: active ? segColor : 'rgba(255,255,255,0.08)',
              boxShadow: active && !muted ? `0 0 6px ${color}60` : 'none',
              opacity: active ? 1 : 0.4,
            }}
          />
        )
      })}
    </div>
  )
}

/** 1 チャンネル分の縦列（数値・スライダー・ミュートボタン） */
const StemChannel = ({
  value,
  onChange,
  onToggleMute,
  label,
  color,
  disabled,
  icon,
}: {
  value: number
  onChange: (volume: number) => void
  onToggleMute: () => void
  label: string
  color: string
  disabled: boolean
  icon: React.ReactNode
}) => {
  const muted = value === 0

  return (
    <div className="flex flex-col items-center gap-2">
      <span className="text-sm font-semibold tabular-nums" style={{ color: muted ? 'rgb(248 113 113 / 0.8)' : color }}>
        {Math.round(value * 100)}%
      </span>
      <VerticalSlider
        value={value}
        onChange={onChange}
        label={`${label}音量`}
        muted={muted}
        color={color}
        disabled={disabled}
      />
      <button
        type="button"
        onClick={onToggleMute}
        disabled={disabled}
        className="flex items-center gap-1 rounded-full border-none bg-white/6 px-3 py-1 disabled:opacity-40"
        aria-label={`${label}ミュート切替`}
        aria-pressed={muted}
      >
        {muted ? <VolumeOff size={12} className="text-red-400/80" /> : icon}
        <span className={cn('text-xs font-semibold', muted && 'text-red-400/80 line-through')}>{label}</span>
      </button>
    </div>
  )
}

export const MusicStemPanel = () => {
  const stemEnabled = useMusicStore((s) => s.stemEnabled)
  const stemsAvailable = useMusicStore((s) => s.stemsAvailable)
  const isStemSwitching = useMusicStore((s) => s.isStemSwitching)
  const vocalVolume = useMusicStore((s) => s.vocalVolume)
  const instVolume = useMusicStore((s) => s.instVolume)
  const setStemEnabled = useMusicStore((s) => s.setStemEnabled)
  const setVocalVolume = useMusicStore((s) => s.setVocalVolume)
  const setInstVolume = useMusicStore((s) => s.setInstVolume)

  // ミュート解除で元の音量に戻せるよう、直前の値を覚えておく
  const prevVocal = useRef(1)
  const prevInst = useRef(1)

  const controlsDisabled = !stemEnabled || !stemsAvailable
  const isMixing = resolveStemPlan({ enabled: stemEnabled, vocalVolume, instVolume, stemsAvailable }).engine === 'mix'

  const toggleVocalMute = () => {
    if (vocalVolume > 0) {
      prevVocal.current = vocalVolume
      setVocalVolume(0)
      return
    }
    setVocalVolume(prevVocal.current || 1)
  }

  const toggleInstMute = () => {
    if (instVolume > 0) {
      prevInst.current = instVolume
      setInstVolume(0)
      return
    }
    setInstVolume(prevInst.current || 1)
  }

  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-6 short:gap-3">
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold">ボーカル分離</span>
        <Switch checked={stemEnabled} onCheckedChange={setStemEnabled} disabled={!stemsAvailable || isStemSwitching} />
        {isStemSwitching && <Loader2 size={16} className="animate-spin text-muted-foreground" />}
      </div>

      {!stemsAvailable && <p className="text-xs text-muted-foreground">この曲はボーカル分離に対応していません</p>}

      <div className="flex items-end gap-8">
        <StemChannel
          value={vocalVolume}
          onChange={setVocalVolume}
          onToggleMute={toggleVocalMute}
          label="Vo"
          color="#818cf8"
          disabled={controlsDisabled}
          icon={<AudioLines size={12} className="text-indigo-300" />}
        />
        <StemChannel
          value={instVolume}
          onChange={setInstVolume}
          onToggleMute={toggleInstMute}
          label="Inst"
          color="#34d399"
          disabled={controlsDisabled}
          icon={<Music size={12} className="text-emerald-300" />}
        />
      </div>

      {/* Web Audio で混ぜる間は iOS の消音スイッチで無音になり、バックグラウンド再生も止まる */}
      {isMixing && (
        <div className="flex max-w-72 items-start gap-2 rounded-lg bg-amber-400/10 px-3 py-2 text-left text-[11px] leading-relaxed text-amber-200">
          <TriangleAlert size={14} className="mt-px shrink-0" />
          <span>iPhone は消音スイッチがオンだと音が出ません。音が聞こえないときは消音を解除してください。</span>
        </div>
      )}

      <p className="max-w-64 text-center text-[11px] leading-relaxed text-muted-foreground">
        どちらかを 0% にするとバックグラウンド再生を続けられます。 中間の音量で混ぜている間は画面を離れると停止します。
      </p>
    </div>
  )
}

/** ステムパネルを開くタブボタン */
export const MusicStemToggle = ({
  active,
  onClick,
  className,
}: {
  active: boolean
  onClick: () => void
  className?: string
}) => {
  const stemEnabled = useMusicStore((s) => s.stemEnabled)
  const isStemSwitching = useMusicStore((s) => s.isStemSwitching)

  return (
    <button
      type="button"
      aria-label="ボーカル分離"
      aria-pressed={active}
      onClick={onClick}
      className={cn('relative border-none p-2', !active && 'text-muted-foreground', className)}
    >
      {isStemSwitching ? <Loader2 size={22} className="animate-spin" /> : <AudioLines size={22} />}
      {stemEnabled && !isStemSwitching && (
        <span className="absolute top-1 right-1 size-1.5 rounded-full bg-indigo-400" />
      )}
    </button>
  )
}
